import React from 'react';
import { isInputField, isFieldVisible } from './engine';
import { FileTile } from './FieldRenderer';

/** Read-only rendering of one answer, for mentor review and the printable copy. */
export default function AnswerView({ field, answer, files, print }) {
    const a = answer || {};
    const empty = <span className="italic text-ink-400">No answer</span>;

    if (field.allow_na && a.na) {
        if (field.type === 'score') {
            return (
                <div className="text-sm text-ink-700">
                    <p>N/A — marked not applicable</p>
                    {a.reason && <p className="mt-1 whitespace-pre-line text-ink-600"><span className="font-medium text-ink-800">Why:</span> {a.reason}</p>}
                </div>
            );
        }
        return <p className="text-sm text-ink-500">N/A — marked not applicable</p>;
    }

    switch (field.type) {
        case 'short_text':
        case 'long_text':
        case 'date':
            return a.value ? <p className="whitespace-pre-line text-sm text-ink-900">{a.value}</p> : empty;
        case 'number':
            return a.value !== '' && a.value !== undefined && a.value !== null ? <p className="text-sm text-ink-900">{a.value}</p> : empty;
        case 'checkbox':
            return <p className="text-sm text-ink-900">{a.value ? 'Yes' : 'No'}</p>;
        case 'single_choice': {
            const opt = (field.options || []).find((o) => o.value === a.value);
            return opt ? <p className="text-sm text-ink-900">{opt.label}{opt.description ? <span className="text-ink-500"> — {opt.description}</span> : null}</p> : empty;
        }
        case 'score':
            return Number.isInteger(a.score) ? (
                <div className="text-sm">
                    <p className="font-semibold text-ink-900">{a.score} / 5</p>
                    {a.reason && <p className="mt-1 whitespace-pre-line text-ink-700">{a.reason}</p>}
                </div>
            ) : empty;
        case 'checklist': {
            const items = field.items || [];
            const checked = new Set(a.checked || []);
            return (
                <div className="text-sm">
                    <p className="font-semibold text-ink-900">{checked.size} / {items.length} ticked</p>
                    {checked.size < items.length && (
                        <ul className="mt-1 list-disc pl-5 text-ink-600">
                            {items.map((item, i) => (!checked.has(i) ? <li key={i}>Not ticked: {item}</li> : null))}
                        </ul>
                    )}
                </div>
            );
        }
        case 'process_steps': {
            const steps = (a.steps || []).filter((s) => String(s || '').trim());
            if (!steps.length && !a.outcome) return empty;
            return (
                <div className="flex flex-wrap items-center gap-1.5 text-xs">
                    <span className="rounded-full bg-ink-950 px-2.5 py-1 font-bold text-white">START</span>
                    {steps.map((s, i) => (
                        <React.Fragment key={i}>
                            <span className="text-ink-400">→</span>
                            <span className="rounded-full bg-ink-100 px-2.5 py-1 font-medium text-ink-900">{s}</span>
                        </React.Fragment>
                    ))}
                    <span className="text-ink-400">→</span>
                    <span className="rounded-full bg-emerald-50 px-2.5 py-1 font-semibold text-emerald-800">{a.outcome ? `OUTCOME: ${a.outcome}` : 'OUTCOME'}</span>
                </div>
            );
        }
        case 'file_upload': {
            const list = files || [];
            if (!list.length) return empty;
            if (print) {
                return (
                    <ul className="text-sm text-ink-800">
                        {list.map((f) => <li key={f.id}>{f.name} {f.consent ? '(permission confirmed)' : '(permission NOT confirmed)'}</li>)}
                    </ul>
                );
            }
            return (
                <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
                    {list.map((f) => (
                        <FileTile key={f.id} file={f} readOnly>
                            <p className={`mt-1 text-[11px] ${f.consent ? 'text-emerald-700' : 'text-red-700'}`}>
                                {f.consent ? `✓ ${field.consent_label || 'Permission confirmed'}` : 'Permission not confirmed'}
                            </p>
                        </FileTile>
                    ))}
                </div>
            );
        }
        default:
            return null;
    }
}

/** "11 answers · 1 N/A", "18 / 25" or "3 files" for a collapsed section. */
export function sectionSummary(section, answers, files, score) {
    const fields = (section.fields || []).filter((f) => isInputField(f) && isFieldVisible(f, answers));
    if (fields.some((f) => f.type === 'score') && score) return `${score.total} / ${score.max}`;
    const fileFields = fields.filter((f) => f.type === 'file_upload');
    if (fileFields.length === fields.length && fileFields.length) {
        const n = (files || []).filter((f) => fileFields.some((ff) => ff.key === f.field_key)).length;
        return `${n} file${n === 1 ? '' : 's'}`;
    }
    let answered = 0;
    let na = 0;
    for (const f of fields) {
        const a = answers[f.key] || {};
        if (f.allow_na && a.na) { na += 1; continue; }
        const has = f.type === 'file_upload'
            ? (files || []).some((x) => x.field_key === f.key)
            : f.type === 'checklist' ? (a.checked || []).length > 0
                : f.type === 'process_steps' ? (a.steps || []).some((s) => String(s || '').trim())
                    : f.type === 'score' ? Number.isInteger(a.score)
                        : f.type === 'checkbox' ? a.value === true
                            : a.value !== undefined && a.value !== null && String(a.value).trim() !== '';
        if (has) answered += 1;
    }
    return `${answered} answer${answered === 1 ? '' : 's'}${na ? ` · ${na} N/A` : ''}`;
}
